import React from 'react'
import { isListingDurationAvailable } from '@popety-io/utils'
import { Icon } from '../../../atoms/Icon'
import { ListingEvent as SaleEventType } from './event.types'
import { truncateText } from './formatUtils'
import {
  EventCard,
  EventHeader,
  EventTitle,
  EventIcon,
  EventStatus,
  EventContent,
  EventDetails,
  EventImage,
  EventPrice,
  EventPriceSub,
  EventDescription,
  EventMeta,
  MetaItem,
  MetaIcon,
  EventFooter,
} from '../HistoryTab.styles'

interface SaleEventProps {
  event: SaleEventType
  translations?: {
    title?: string
    fields?: {
      address?: string
      propertyType?: string
      propertyCategory?: string
      area?: string
      landArea?: string
      rooms?: string
      bathrooms?: string
      floor?: string
      constructionYear?: string
      renovationYear?: string
      advertiser?: string
      broker?: string
      garage?: string
      parking?: string
      balcony?: string
      terrace?: string
      garden?: string
      elevator?: string
      energyLabel?: string
      heatingType?: string
    }
    status?: {
      active?: string
      inactive?: string
    }
    footer?: {
      online?: string
      days?: string
    }
  }
  formatPrice?: (price: number) => string
  formatPricePerSqm?: (price: number) => string
}

export const SaleEventComponent: React.FC<SaleEventProps> = ({
  event,
  translations,
  formatPrice,
  formatPricePerSqm,
}) => {
  const features = []
  if (event.garage) features.push(translations?.fields?.garage || 'Garage')
  if (event.parking) features.push(translations?.fields?.parking || 'Parking')
  if (event.balcony) features.push(translations?.fields?.balcony || 'Balcony')
  if (event.terrace) features.push(translations?.fields?.terrace || 'Terrace')
  if (event.garden) features.push(translations?.fields?.garden || 'Garden')
  if (event.elevator)
    features.push(translations?.fields?.elevator || 'Elevator')

  const statusText =
    event.status === 'Active'
      ? translations?.status?.active || 'Active'
      : translations?.status?.inactive || 'Inactive'

  return (
    <EventCard $type="sale">
      <EventHeader>
        <EventTitle>
          <EventIcon>
            <Icon name="listing-purchase" size="small" />
          </EventIcon>
          {translations?.title || 'Sale Listing'}
        </EventTitle>
        <EventStatus $status={event.status}>{statusText}</EventStatus>
      </EventHeader>

      <EventContent>
        <EventDetails>
          {event.price > 0 && (
            <>
              <EventPrice>{formatPrice(event.price)}</EventPrice>
              {event.pricePerSqm && (
                <EventPriceSub>
                  {formatPricePerSqm(event.pricePerSqm)}
                </EventPriceSub>
              )}
            </>
          )}

          {/* Truncate description to 100 characters */}
          {event.description && (
            <EventDescription>
              {truncateText(event.description, 100)}
            </EventDescription>
          )}

          <EventMeta>
            {event.propertyType && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </MetaIcon>
                {event.propertyType}
                {event.propertyCategory && ` - ${event.propertyCategory}`}
              </MetaItem>
            )}

            {event.area && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M4 4h16v16H4z" />
                </MetaIcon>
                {translations?.fields?.area || 'Living area'}: {event.area} m²
              </MetaItem>
            )}

            {event.landArea && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M20 6c-.58-3.06-2.95-5.43-6.01-6.01a7.002 7.002 0 00-6.9 11.11L12 16l4.91-4.9C18.05 9.94 20 8.35 20 6zm-8 6c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
                </MetaIcon>
                {translations?.fields?.landArea || 'Land'}: {event.landArea} m²
              </MetaItem>
            )}

            {event.rooms && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </MetaIcon>
                {event.rooms} {translations?.fields?.rooms || 'rooms'}
              </MetaItem>
            )}

            {event.bathrooms && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 7v4.5l-1.5 1.5L6 11.5V7c0-.55.45-1 1-1h1c.55 0 1 .45 1 1z" />
                </MetaIcon>
                {event.bathrooms}{' '}
                {translations?.fields?.bathrooms || 'bathrooms'}
              </MetaItem>
            )}

            {event.floor !== undefined && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
                </MetaIcon>
                {translations?.fields?.floor || 'Floor'} {event.floor}
              </MetaItem>
            )}

            {event.constructionYear && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </MetaIcon>
                {translations?.fields?.constructionYear || 'Built'}:{' '}
                {event.constructionYear}
              </MetaItem>
            )}

            {event.renovationYear && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12.09 2.91C10.08.9 7.07.49 4.65 1.67l4.34 4.34-3 3-4.34-4.34C.48 7.1.89 10.09 2.9 12.1c1.86 1.86 4.58 2.35 6.89 1.48l9.82 9.82 3.71-3.71-9.78-9.79c.92-2.34.44-5.1-1.45-6.99z" />
                </MetaIcon>
                {translations?.fields?.renovationYear || 'Renovated'}:{' '}
                {event.renovationYear}
              </MetaItem>
            )}

            {event.energyLabel && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M13 2.05v3.03c3.39.49 6 3.39 6 6.92 0 .9-.18 1.75-.48 2.54l2.6 1.53c.56-1.24.88-2.62.88-4.07 0-5.18-3.95-9.45-9-9.95zM12 19c-3.87 0-7-3.13-7-7 0-3.53 2.61-6.43 6-6.92V2.05c-5.06.5-9 4.76-9 9.95 0 5.52 4.47 10 9.99 10 3.31 0 6.24-1.61 8.06-4.09l-2.6-1.53C16.17 17.98 14.21 19 12 19z" />
                </MetaIcon>
                {translations?.fields?.energyLabel || 'Energy'}:{' '}
                {event.energyLabel}
              </MetaItem>
            )}

            {event.heatingType && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-8l-2 3v6h2v9h3v-9h2v9h3v-9h2v-6l-2-3z" />
                </MetaIcon>
                {translations?.fields?.heatingType || 'Heating'}:{' '}
                {event.heatingType}
              </MetaItem>
            )}

            {features.length > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </MetaIcon>
                {features.join(', ')}
              </MetaItem>
            )}

            {event.brokerName && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </MetaIcon>
                {translations?.fields?.broker || 'Broker'}: {event.brokerName}
                {event.brokerCompany && ` (${event.brokerCompany})`}
              </MetaItem>
            )}
          </EventMeta>
        </EventDetails>

        {event.imageUrls && event.imageUrls[0] && (
          <EventImage>
            <img
              src={event.imageUrls[0]}
              alt={event.propertyType || 'Property'}
            />
          </EventImage>
        )}
      </EventContent>

      {isListingDurationAvailable(event.onlineDays) && (
        <EventFooter>
          {translations?.footer?.online || 'Online'} {event.onlineDays}{' '}
          {translations?.footer?.days || 'days'}
        </EventFooter>
      )}
    </EventCard>
  )
}
